/* ─── Artifact state management (store + reducer + hooks) ───
 *
 * Render granularity: each <RenderNode> subscribes via useResolvedProps(),
 * which computes fully resolved properties inside a useSyncExternalStore
 * snapshot. The snapshot is shallow-compared, so a node only re-renders
 * when its own resolved values change — not when unrelated components or
 * data model keys are updated. See artifact/REACT.md for details.
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import type {
  ArtifactAction,
  ArtifactState,
  ComponentMsg,
  ComponentNode,
  RefMsg,
  ThemeConfig,
  ValueMsg,
} from './types';

// ─── Initial state ──────────────────────────────────────────────────────

/** Component kinds whose `value` property is user-controlled and should
 *  be seeded into liveValues on SURFACE_UPDATE for cross-component Ref resolution. */
const INTERACTIVE_KINDS = new Set(['Slider', 'CheckBox', 'TextField', 'ChoicePicker', 'Tabs']);

export const INITIAL_STATE: ArtifactState = {
  roots: {},
  components: new Map(),
  dataModel: {},
  liveValues: new Map(),
  theme: {},
  connected: false,
  pages: [],
  viewingPageIndex: null,
};

// ─── Wire format helpers ────────────────────────────────────────────────

/**
 * Unwrap result: the raw value plus whether it may contain Refs that
 * need resolution against the data model.
 */
export interface UnwrappedValue {
  value: unknown;
  /** True only for 'any'-tagged values that may embed Refs. */
  mayContainRefs: boolean;
}

/** Unwrap a ValueMsg, tracking whether it may contain Refs. */
export function unwrapValue(msg: ValueMsg): UnwrappedValue | undefined {
  switch (msg['$1/tag']) {
    case 'json':
      return { value: msg.value, mayContainRefs: false };
    case 'any':
      return { value: msg.value, mayContainRefs: true };
    case 'unspecified':
      return undefined;
  }
}

/** Check if a value is a Ref message. */
export function isRef(value: unknown): value is RefMsg {
  return (
    value != null &&
    typeof value === 'object' &&
    (value as Record<string, unknown>)['$1/tag'] === 'Ref'
  );
}

/** Convert a wire ComponentMsg into a client-side ComponentNode. */
export function componentMsgToNode(msg: ComponentMsg): ComponentNode {
  const properties: Record<string, unknown> = {};
  const boundProps = new Set<string>();
  for (const prop of msg.props) {
    const unwrapped = unwrapValue(prop.value);
    if (unwrapped !== undefined) {
      properties[prop.name] = unwrapped.value;
      if (unwrapped.mayContainRefs) {
        boundProps.add(prop.name);
      }
    }
  }
  const callbacks: Record<string, number> = {};
  for (const cb of msg.cbs) {
    callbacks[cb.name] = cb.value;
  }
  return {
    cuid: msg.cuid,
    component: msg.kind,
    properties,
    callbacks,
    subs: msg.subs ?? {},
    boundProps,
    clock: msg.clock,
  };
}

/** Unwrap a wire data model (name → ValueMsg) to plain values. */
export function unwrapDataModel(data: Record<string, ValueMsg>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, msg] of Object.entries(data)) {
    const unwrapped = unwrapValue(msg);
    result[key] = unwrapped?.value;
  }
  return result;
}

// ─── Reducer ────────────────────────────────────────────────────────────

export function artifactReducer(
  state: ArtifactState,
  action: ArtifactAction,
): ArtifactState {
  switch (action.type) {
    case 'BEGIN_RENDERING':
      return {
        ...state,
        theme: action.theme,
        roots: {},
        components: new Map(),
        dataModel: {},
      };

    case 'SURFACE_UPDATE': {
      const next = new Map(state.components);
      const live = new Map(state.liveValues);
      for (const comp of action.components) {
        next.set(comp.cuid, comp);
        // Seed live values for interactive components so cross-component
        // Refs resolve immediately without waiting for a useEffect.
        if (INTERACTIVE_KINDS.has(comp.component) && 'value' in comp.properties) {
          const prev = live.get(comp.cuid);
          if (!prev || !(('value') in prev)) {
            live.set(comp.cuid, { ...prev, value: comp.properties.value });
          }
        }
      }
      return { ...state, roots: { ...state.roots, ...action.roots }, components: next, liveValues: live };
    }

    case 'DATA_MODEL_UPDATE':
      return { ...state, dataModel: action.data };

    case 'UPDATE_CALLBACKS': {
      const next = new Map(state.components);
      for (const binding of action.bindings) {
        const node = next.get(binding.comp_id);
        if (node) {
          const updated = { ...node, callbacks: { ...node.callbacks } };
          if (binding.rem) {
            delete updated.callbacks[binding.cb_name];
          } else {
            updated.callbacks[binding.cb_name] = binding.cb_id;
          }
          next.set(binding.comp_id, updated);
        }
      }
      return { ...state, components: next };
    }

    case 'DELETE_SURFACE': {
      if (!action.comp_ids || action.comp_ids.length === 0) {
        return { ...state, roots: {}, components: new Map(), dataModel: {} };
      }
      const toDelete = new Set(action.comp_ids);
      const next = new Map(state.components);
      for (const cuid of toDelete) {
        next.delete(cuid);
      }
      // Also remove from roots
      const nextRoots = { ...state.roots };
      for (const [name, cuid] of Object.entries(nextRoots)) {
        if (toDelete.has(cuid)) delete nextRoots[name];
      }
      return { ...state, roots: nextRoots, components: next };
    }

    case 'NEW_PAGE': {
      const hasContent = state.components.size > 0;
      const snapshot = hasContent
        ? {
            label: action.label,
            roots: { ...state.roots },
            components: new Map(state.components),
            dataModel: { ...state.dataModel },
            theme: { ...state.theme },
          }
        : null;
      return {
        ...state,
        pages: snapshot ? [...state.pages, snapshot] : state.pages,
        roots: {},
        components: new Map(),
        dataModel: {},
        viewingPageIndex: null,
      };
    }

    case 'DELETE_PAGE': {
      const nextPages = state.pages.filter((_, i) => i !== action.index);
      let nextViewingIndex = state.viewingPageIndex;
      if (nextViewingIndex !== null) {
        if (nextViewingIndex === action.index) {
          nextViewingIndex = null;
        } else if (nextViewingIndex > action.index) {
          nextViewingIndex -= 1;
        }
      }
      return { ...state, pages: nextPages, viewingPageIndex: nextViewingIndex };
    }

    case 'SET_VIEWING_PAGE':
      return { ...state, viewingPageIndex: action.index };

    case 'SET_LIVE_VALUE': {
      const next = new Map(state.liveValues);
      const prev = next.get(action.cuid);
      next.set(action.cuid, { ...prev, [action.name]: action.value });
      return { ...state, liveValues: next };
    }

    case 'SET_CONNECTED':
      return { ...state, connected: action.connected };

    case 'RESET':
      return { ...INITIAL_STATE };

    default:
      return state;
  }
}

// ─── Compute root IDs ───────────────────────────────────────────────────

/** Return root cuids in insertion order. */
export function computeRootCuids(roots: Record<string, number>): number[] {
  return Object.values(roots);
}

// ─── Ref resolution ─────────────────────────────────────────────────────

/**
 * Follow a Ref's path into a value.
 * path is an array like ['field', 0, 'subfield'].
 */
function followPath(value: unknown, path: (string | number)[]): unknown {
  let cursor = value;
  for (const part of path) {
    if (cursor == null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string | number, unknown>)[part];
  }
  return cursor;
}

/**
 * Resolve a Ref against the data model.
 * A Ref has {root, name, path} — `name` looks up a top-level data model
 * entry, then `path` drills into it.
 */
export function resolveRef(
  ref: RefMsg,
  dataModel: Record<string, unknown>,
  liveValues?: Map<number, Record<string, unknown>>,
): unknown {
  // 1. Live values (interactive component state, seeded on SURFACE_UPDATE
  //    and updated on user interaction)
  const live = liveValues?.get(ref.root);
  if (live && ref.name in live) {
    const base = live[ref.name];
    return ref.path.length === 0 ? base : followPath(base, ref.path);
  }
  // 2. Data model (DataStore variables)
  const base = dataModel[ref.name];
  if (ref.path.length === 0) return base;
  return followPath(base, ref.path);
}

/**
 * Recursively resolve Refs within a value against the data model.
 * - If the value itself is a Ref, resolve it.
 * - If it's an object or array, walk it looking for nested Refs.
 * - Plain scalars pass through unchanged.
 */
export function resolveValue(
  value: unknown,
  dataModel: Record<string, unknown>,
  liveValues?: Map<number, Record<string, unknown>>,
): unknown {
  if (isRef(value)) {
    return resolveRef(value, dataModel, liveValues);
  }
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((item) => {
      const resolved = resolveValue(item, dataModel, liveValues);
      if (resolved !== item) changed = true;
      return resolved;
    });
    return changed ? out : value;
  }
  if (value != null && typeof value === 'object') {
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const resolved = resolveValue(v, dataModel, liveValues);
      out[k] = resolved;
      if (resolved !== v) changed = true;
    }
    return changed ? out : value;
  }
  return value;
}

/**
 * Walk properties and resolve any Ref values against the data model.
 * Only traverses properties listed in `boundProps` (those that came from
 * AnyValueMsg and may contain Refs). Pure JSON properties are passed through.
 */
export function resolveBindings(
  props: Record<string, unknown>,
  boundProps: Set<string>,
  dataModel: Record<string, unknown>,
  liveValues?: Map<number, Record<string, unknown>>,
): Record<string, unknown> {
  if (boundProps.size === 0) return props;
  const resolved: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    resolved[key] = boundProps.has(key) ? resolveValue(value, dataModel, liveValues) : value;
  }
  return resolved;
}

// ─── External store ─────────────────────────────────────────────────────

/**
 * Mutable store that holds artifact state outside the React tree.
 * Components subscribe via `useSyncExternalStore` with fine-grained
 * selectors so they only re-render when their slice of state changes.
 */
export class ArtifactStore {
  private _state: ArtifactState = { ...INITIAL_STATE };
  private _listeners = new Set<() => void>();

  getState = (): ArtifactState => this._state;

  dispatch = (action: ArtifactAction): void => {
    this._state = artifactReducer(this._state, action);
    for (const l of this._listeners) l();
  };

  subscribe = (listener: () => void): (() => void) => {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  };

  getEffectiveRoots = (): Record<string, number> => {
    const { viewingPageIndex, pages, roots } = this._state;
    if (viewingPageIndex !== null && pages[viewingPageIndex]) {
      return pages[viewingPageIndex].roots;
    }
    return roots;
  };

  getEffectiveComponents = (): Map<number, ComponentNode> => {
    const { viewingPageIndex, pages, components } = this._state;
    if (viewingPageIndex !== null && pages[viewingPageIndex]) {
      return pages[viewingPageIndex].components;
    }
    return components;
  };

  getEffectiveDataModel = (): Record<string, unknown> => {
    const { viewingPageIndex, pages, dataModel } = this._state;
    if (viewingPageIndex !== null && pages[viewingPageIndex]) {
      return pages[viewingPageIndex].dataModel;
    }
    return dataModel;
  };

  getEffectiveTheme = (): ThemeConfig => {
    const { viewingPageIndex, pages, theme } = this._state;
    if (viewingPageIndex !== null && pages[viewingPageIndex]) {
      return pages[viewingPageIndex].theme;
    }
    return theme;
  };
}

// ─── Contexts ───────────────────────────────────────────────────────────

export const ArtifactStoreContext = createContext<ArtifactStore | null>(null);

export interface ArtifactCallbacks {
  sendEvent: (comp_id: string, event: string, data: unknown) => void;
  renderNode: (nodeId: string) => ReactNode;
}

export const ArtifactCallbacksContext = createContext<ArtifactCallbacks | null>(null);

// ─── Hooks ──────────────────────────────────────────────────────────────

function useStore(): ArtifactStore {
  const store = useContext(ArtifactStoreContext);
  if (!store) throw new Error('Artifact hooks must be used inside ArtifactProvider');
  return store;
}

/** Subscribe to a specific component node by cuid. Only re-renders when that node changes. */
export function useArtifactNode(cuid: number): ComponentNode | undefined {
  const store = useStore();
  const getSnapshot = useCallback(
    () => store.getEffectiveComponents().get(cuid),
    [store, cuid],
  );
  return useSyncExternalStore(store.subscribe, getSnapshot);
}

/** Subscribe to the effective data model. Re-renders only when the model reference changes. */
export function useArtifactDataModel(): Record<string, unknown> {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, store.getEffectiveDataModel);
}

/**
 * Subscribe to the fully resolved properties of a single component.
 * Only re-renders when the resolved values actually change (shallow comparison).
 * This avoids re-rendering every node when unrelated components or data keys change.
 */
export interface ResolvedSnapshot {
  node: ComponentNode | undefined;
  resolved: Record<string, unknown> | null;
  /** Inputs to resolveBindings, held so the next call can short-circuit. */
  dataModel: Record<string, unknown> | null;
  liveValues: Map<number, Record<string, unknown>> | null;
}

const EMPTY_SNAPSHOT: ResolvedSnapshot = {
  node: undefined,
  resolved: null,
  dataModel: null,
  liveValues: null,
};

/**
 * Pure snapshot function used by `useResolvedProps`. Extracted so the
 * caching behavior can be tested directly without a React renderer.
 *
 * Must return `prev` (same reference) whenever no inputs have changed,
 * otherwise `useSyncExternalStore` will schedule re-renders in a loop
 * and React throws error #185 "Maximum update depth exceeded".
 *
 * `node`, `dataModel`, and `liveValues` are references stored inside the
 * artifact state object — the reducer swaps them atomically when they
 * change, so a triple reference match means nothing a bound prop depends
 * on has changed and we can return the previous snapshot unchanged. The
 * shallowEqual fallback below exists for the rare case where two state
 * transitions produce the same resolved values but different container
 * references (e.g. liveValues Map rebuilt with identical entries).
 */
export function computeResolvedSnapshot(
  prev: ResolvedSnapshot,
  node: ComponentNode | undefined,
  dataModel: Record<string, unknown>,
  liveValues: Map<number, Record<string, unknown>>,
): ResolvedSnapshot {
  if (!node) {
    if (prev.node === undefined && prev.resolved === null) return prev;
    return EMPTY_SNAPSHOT;
  }
  if (node.boundProps.size === 0) {
    if (prev.node === node) return prev;
    return { node, resolved: node.properties, dataModel: null, liveValues: null };
  }
  if (
    prev.node === node &&
    prev.dataModel === dataModel &&
    prev.liveValues === liveValues
  ) {
    return prev;
  }
  const resolved = resolveBindings(node.properties, node.boundProps, dataModel, liveValues);
  if (prev.node === node && prev.resolved && shallowEqual(prev.resolved, resolved)) {
    return prev;
  }
  return { node, resolved, dataModel, liveValues };
}

export function useResolvedProps(cuid: number): ResolvedSnapshot {
  const store = useStore();
  const cacheRef = useRef<ResolvedSnapshot>(EMPTY_SNAPSHOT);

  const getSnapshot = useCallback(() => {
    const node = store.getEffectiveComponents().get(cuid);
    const dataModel = store.getEffectiveDataModel();
    const liveValues = store.getState().liveValues;
    const next = computeResolvedSnapshot(cacheRef.current, node, dataModel, liveValues);
    cacheRef.current = next;
    return next;
  }, [store, cuid]);

  return useSyncExternalStore(store.subscribe, getSnapshot);
}

function shallowEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

/** Get the store's dispatch function. Never causes re-renders. */
export function useArtifactDispatch(): ArtifactStore['dispatch'] {
  const store = useStore();
  return store.dispatch;
}

/** Get stable sendEvent callback. Never causes re-renders from state changes. */
export function useArtifactSendEvent(): ArtifactCallbacks['sendEvent'] {
  const ctx = useContext(ArtifactCallbacksContext);
  if (!ctx) throw new Error('useArtifactSendEvent must be used inside ArtifactProvider');
  return ctx.sendEvent;
}

/** Get stable renderNode callback. Never causes re-renders from state changes. */
export function useArtifactRenderNode(): ArtifactCallbacks['renderNode'] {
  const ctx = useContext(ArtifactCallbacksContext);
  if (!ctx) throw new Error('useArtifactRenderNode must be used inside ArtifactProvider');
  return ctx.renderNode;
}
